/**
 * Which weapons a given critter's art can actually be shown holding — the
 * engine's own wield test, ported rather than remembered.
 *
 * ## The rule, and where it lives
 *
 * Fallout 2 has no companion→weapon table. A party member "cannot use rifles"
 * only because nobody drew that companion holding a rifle: the wield attempt
 * builds a FID out of (the critter's art, the weapon's animation code, the
 * weapon's primary attack animation) and asks whether the resulting `.frm` is
 * in the install. If it is not, the equip is refused.
 *
 * `inventoryEquipFunc`, engine/src/inventory.cc:3941-3947, is the whole gate:
 *
 *     WeaponAnimation weaponAnimationCode = weaponGetAnimationCode(item);
 *     AnimationType hitModeAnimationCode =
 *         weaponGetAnimationForHitMode(item, HIT_MODE_RIGHT_WEAPON_PRIMARY);
 *     int fid = buildFid(OBJ_TYPE_CRITTER, critter->fid & 0xFFF,
 *                        hitModeAnimationCode, weaponAnimationCode,
 *                        critter->rotation + 1);
 *     if (!artExists(fid)) { ... return -1; }
 *
 * `critterCanUseWeapon` (engine/src/critter.cc:1029) computes the same FID and
 * the same `artExists`, with two extra refusals for crippled arms — combat
 * state, not install state, so not modelled here.
 *
 * The pieces it is made of:
 *
 *   - `weaponGetAnimationCode` (engine/src/item.cc:1848) returns
 *     `proto->item.data.weapon.animationCode`, i.e. the catalog's
 *     `fields['animationCode']`. 0..15, per `WeaponAnimation`
 *     (engine/src/art_defs.h:100-118).
 *   - `weaponGetAnimationForHitMode` (engine/src/item.cc:1403) indexes
 *     `_attack_anim` (item.cc:120) with the *primary* attack-mode nibble,
 *     `extendedFlags & 0xF`. This is why the probed animation differs per
 *     weapon: a knife probes the swing anim, a rifle the single-fire anim, a
 *     grenade the throw anim.
 *   - `buildFid` (engine/src/art.cc:1088) forces `ROTATION_NE` for every
 *     animation outside the knockdown/death band, so the wield probe always
 *     tests the rotation-0 file — `.frm`, never `.fr1`..`.fr5`.
 *   - `artBuildFilePath` (engine/src/art.cc:667) turns that FID into
 *     `art\critters\<lstBaseName><weaponLetter><animLetter>.frm`, where the two
 *     letters come from `_art_get_code` (art.cc:578) and
 *     `artGetCritterWeaponCode` (art.cc:648).
 *   - `artExists` (engine/src/art.cc:927) is a plain `dbGetFileSize` on that
 *     path: a real per-file probe against the DAT index, not a `.lst` lookup.
 *     The `.lst` supplies only the base name for `fid & 0xFFF`.
 *
 * ## What is deliberately not modelled
 *
 * The engine probes `critter->fid & 0xFFF` — the critter's *current* art, which
 * on an armoured critter is the armour's `maleFid`/`femaleFid`
 * (`inventoryComputeCritterFid`, engine/src/inventory.cc:1557-1590). The editor
 * only knows the owner's pid, so this uses the base critter proto's own art.
 * For the player that is the same answer either way (every PC base ships the
 * full weapon set — verified against a stock install, not assumed), and for
 * companions vanilla armour does not swap their art.
 *
 * Nothing here says whether a companion *should* get a weapon. Handing over an
 * unwieldable gun is legal — they carry it and cannot equip it — so this module
 * exists to label, never to refuse.
 */

/** `AnimationType`, engine/src/animation.h — only the values this file uses. */
const ANIM_WALK = 1;
const ANIM_THROW_ANIM = 18;
const ANIM_DODGE_ANIM = 13;
const ANIM_PRONE_TO_STANDING = 36;
const ANIM_BACK_TO_STANDING = 37;
const ANIM_TAKE_OUT = 38;
const ANIM_FIRE_CONTINUOUS = 47;
const ANIM_CALLED_SHOT_PIC = 122;
const FIRST_KNOCKDOWN_AND_DEATH_ANIM = 20;
const FIRST_SF_DEATH_ANIM = 48;
const LAST_SF_DEATH_ANIM = 63;

/** `_attack_anim`, engine/src/item.cc:120. Indexed by an attack-mode nibble. */
const ATTACK_ANIM = [
  0, // ANIM_STAND
  16, // ANIM_THROW_PUNCH
  17, // ANIM_KICK_LEG
  42, // ANIM_SWING_ANIM
  41, // ANIM_THRUST_ANIM
  18, // ANIM_THROW_ANIM
  45, // ANIM_FIRE_SINGLE
  46, // ANIM_FIRE_BURST
  47, // ANIM_FIRE_CONTINUOUS
] as const;

/**
 * `WeaponAnimation`, engine/src/art_defs.h:100-118, in player words. Index 0 is
 * "no weapon animation" — the code carried by unarmed weapons, grenades and
 * thrown items, which are drawn with the critter's bare-handed art.
 *
 * Note 7 and 8 are wider than their engine names suggest: 7 (`j`) is every
 * long gun, shotgun *and* rifle alike, and 8 (`k`) is the two-handed big-gun
 * set — flamers and miniguns-that-are-not-minigun-coded live there too.
 */
export const WEAPON_ANIMATION_LABELS: readonly string[] = [
  'unarmed / thrown',
  'knife',
  'club',
  'hammer',
  'spear',
  'pistol',
  'SMG',
  'rifle / shotgun',
  'big gun',
  'minigun',
  'rocket launcher',
  'sfall s',
  'sfall o',
  'sfall p',
  'sfall q',
  'sfall t',
];

/** `weaponAnimationIsValid`, engine/src/art_defs.h:129. */
const weaponAnimationIsValid = (code: number): boolean =>
  Number.isInteger(code) && code >= 0 && code < 16;

/** `artGetCritterWeaponCode`, engine/src/art.cc:648. */
function critterWeaponLetter(code: number): string {
  switch (code) {
    case 11: return 's';
    case 12: return 'o';
    case 13: return 'p';
    case 14: return 'q';
    case 15: return 't';
    default: return String.fromCharCode(0x64 /* 'd' */ + code - 1);
  }
}

/**
 * `_art_get_code`, engine/src/art.cc:578. Returns `[weaponLetter, animLetter]`,
 * or null where the engine returns -1 (which makes `artExists` false, so the
 * wield is refused).
 */
export function critterArtCode(animation: number, weaponCode: number): [string, string] | null {
  if (!weaponAnimationIsValid(weaponCode)) return null;

  if (animation >= ANIM_TAKE_OUT && animation <= ANIM_FIRE_CONTINUOUS) {
    // The weapon-handling band. Code 0 has no letter here, which is exactly why
    // a grenade is never "wielded" in the animation sense.
    if (weaponCode === 0) return null;
    return [critterWeaponLetter(weaponCode), String.fromCharCode(0x63 /* 'c' */ + (animation - ANIM_TAKE_OUT))];
  }
  if (animation === ANIM_PRONE_TO_STANDING) return ['c', 'h'];
  if (animation === ANIM_BACK_TO_STANDING) return ['c', 'j'];
  if (animation === ANIM_CALLED_SHOT_PIC) return ['n', 'a'];
  if (animation >= FIRST_SF_DEATH_ANIM && animation <= LAST_SF_DEATH_ANIM) {
    return ['r', String.fromCharCode(0x61 + (animation - FIRST_SF_DEATH_ANIM))];
  }
  if (animation >= FIRST_KNOCKDOWN_AND_DEATH_ANIM && animation < FIRST_SF_DEATH_ANIM) {
    return ['b', String.fromCharCode(0x61 + (animation - FIRST_KNOCKDOWN_AND_DEATH_ANIM))];
  }
  if (animation === ANIM_THROW_ANIM) {
    if (weaponCode === 1) return ['d', 'm'];
    if (weaponCode === 4) return ['g', 'm'];
    return ['a', 's'];
  }
  if (animation === ANIM_DODGE_ANIM) {
    if (weaponCode <= 0) return ['a', 'n'];
    return [critterWeaponLetter(weaponCode), 'e'];
  }

  const animLetter = String.fromCharCode(0x61 + animation);
  if (animation <= ANIM_WALK && weaponCode > 0) return [critterWeaponLetter(weaponCode), animLetter];
  return ['a', animLetter];
}

/**
 * The animation the engine probes when this weapon is put in a hand:
 * `_attack_anim[extendedFlags & 0xF]`.
 */
export function primaryAttackAnimation(extendedFlags: number): number {
  return ATTACK_ANIM[extendedFlags & 0xF] ?? 0;
}

/**
 * The file `artExists` would be asked about, lowercased and forward-slashed the
 * way `GameFiles` addresses archive members. Null when the engine's own path
 * builder would refuse.
 *
 * `base` is the `art/critters/critters.lst` entry for `fid & 0xFFF` — six
 * characters, no extension, e.g. `nmwarr`.
 */
export function wieldArtPath(base: string, weaponCode: number, extendedFlags: number): string | null {
  const code = critterArtCode(primaryAttackAnimation(extendedFlags), weaponCode);
  if (!code) return null;
  return `art/critters/${base}${code[0]}${code[1]}.frm`.toLowerCase();
}
